<template>
  <div class="pft-feedback-tool-tip" :style="tipStyle">
    <div
      v-if="contentDomShow"
      :class="{
        'pft-feedback-tool-tip-container': true,
        [props.position]: true,
      }"
      :style="tooltipStyleComputed"
    >
      <div class="pft-feedback-tool-tip-title">{{ tooltipVal.title }}</div>
      <div v-if="tooltipVal.contentHtml" class="pft-feedback-tool-tip-content" v-html="tooltipVal.contentHtml"></div>
      <div v-else class="pft-feedback-tool-tip-content">{{ tooltipVal.content }}</div>
    </div>
    <div v-if="props.placeholderHtml" class="pft-feedback-tool-tip-placeholder" :style="props.placeholderStyle" v-html="props.placeholderHtml"></div>
    <div v-else class="pft-feedback-tool-tip-placeholder" :style="props.placeholderStyle">{{ props.placeholder }}</div>
  </div>
</template>

<script lang="ts" setup>
import { ref, CSSProperties, computed, watch, nextTick } from "vue"
import { debounce } from "lodash-es"

enum ToolTipPosition {
  topCenter = "top-center",
  topLeft = "top-left",
  topRight = "top-right",
  bottomCenter = "bottom-center",
  bottomLeft = "bottom-left",
  bottomRight = "bottom-right",
  leftCenter = "left-center",
  leftTop = "left-top",
  leftBottom = "left-bottom",
  rightCenter = "right-center",
  rightTop = "right-top",
  rightBottom = "right-bottom",
}

interface Props {
  placeholder?: string
  placeholderHtml?: string
  tooltip: {
    title: string
    content?: string
    contentHtml?: string
  }
  tooltipStyle?: CSSProperties
  placeholderStyle?: CSSProperties
  style?: CSSProperties
  position?: ToolTipPosition
  duration?: number
  data?: { data: Props["tooltip"]; isShow: boolean }
}

const contentShow = ref(false)
const contentDomShow = ref(false)

const props = withDefaults(defineProps<Props>(), {
  tooltipStyle: () => ({}),
  placeholderStyle: () => ({}),
  style: () => ({}),
  position: ToolTipPosition.topCenter,
  duration: 300,
  data: undefined,
})

const tooltipStyleComputed = ref({})

const tipStyle = computed(() => {
  return {
    ...props.style,
    "--pft-feedback-tool-tip-duration": `${props.duration}ms`,
    zIndex: contentShow.value ? 9999 : props.style?.zIndex || 5,
  }
})

const tooltipVal = ref(props.tooltip)

watch(
  () => contentShow.value,
  newVal => {
    if (newVal) {
      contentDomShow.value = true
      tooltipStyleComputed.value = {
        ...props.tooltipStyle,
        opacity: 0,
      }
      setTimeout(() => {
        tooltipStyleComputed.value = {
          ...props.tooltipStyle,
          opacity: 1,
        }
      }, 100)
    } else {
      tooltipStyleComputed.value = {
        ...props.tooltipStyle,
        opacity: 0,
      }
      setTimeout(() => {
        contentDomShow.value = false
      }, props.duration)
    }
  },
  {
    immediate: true,
  },
)

// const tooltipStyleComputed = computed(() => {
//   return {
//     ...props.tooltipStyle,
//     opacity: contentShow.value ? 1 : 0,
//   }
// })

const show = () => {
  contentShow.value = true
}

const hide = () => {
  contentShow.value = false
}

const setData2 = (opt: { data: Props["tooltip"]; isShow: boolean }) => {
  console.log("tooltip setData", opt)
  tooltipVal.value = opt.data
  nextTick(() => {
    contentShow.value = !!opt.isShow
  })
}

const debounceSetData = debounce((opt: { data: Props["tooltip"]; isShow: boolean }) => {
  setData2(opt)
}, 300)

const setData = (opt: { data: Props["tooltip"]; isShow: boolean }) => {
  debounceSetData(opt)
}

watch(
  () => props.data,
  newVal => {
    if (newVal) {
      debounceSetData(newVal)
    }
  },
  {
    immediate: true,
    deep: true,
  },
)

defineExpose({
  show,
  hide,
  contentShow,
  setData,
})
</script>

<style lang="scss">
.pft-feedback-tool-tip {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  &-container {
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    width: max-content;
    height: max-content;
    background: linear-gradient(180deg, #092c3f 0%, rgba(3, 102, 173, 0.85) 100%);
    border-radius: 8px 8px 8px 8px;
    border: 2px solid rgba(43, 151, 247, 0.85);
    transition: all var(--pft-feedback-tool-tip-duration, 0.3s) ease-in-out;
    &.top-center {
      top: -8px;
      left: 50%;
      transform: translateX(-50%) translateY(-100%);
    }
    &.top-left {
      top: -8px;
      left: 0;
      transform: translateX(-100%) translateY(-100%);
    }
    &.top-right {
      top: -8px;
      right: 0;
      transform: translateX(100%) translateY(-100%);
    }
    &.bottom-center {
      bottom: -8px;
      left: 50%;
      transform: translateX(-50%) translateY(100%);
    }
    &.bottom-left {
      bottom: -8px;
      left: 0;
      transform: translateX(-100%) translateY(100%);
    }
    &.bottom-right {
      bottom: -8px;
      right: 0;
      transform: translateX(100%) translateY(100%);
    }
    &.left-center {
      top: 50%;
      left: -8px;
      transform: translateY(-50%) translateX(-100%);
    }
    &.left-top {
      top: 0;
      left: -8px;
    }
    &.left-bottom {
      bottom: 0;
      left: -8px;
    }
    &.right-center {
      top: 50%;
      right: -8px;
      transform: translateY(-50%) translateX(100%);
    }
    &.right-top {
      top: 0;
      right: -8px;
      transform: translateY(-100%) translateX(100%);
    }
    &.right-bottom {
      bottom: 0;
      right: -8px;
      transform: translateY(100%) translateX(100%);
    }
  }
  &-title {
    font-family:
      Segoe UI,
      Segoe UI;
    font-weight: bold;
    font-size: 18px;
    color: #ffffff;
    line-height: 21px;
    text-align: left;
    font-style: normal;
    text-transform: none;
    border-bottom: 1px solid #ffffff33;
    padding: 13px 16px 11px;
  }
  &-content {
    padding: 11px 16px 13px;
    font-family:
      Segoe UI,
      Segoe UI;
    font-weight: 400;
    font-size: 14px;
    color: #ffffff;
    line-height: 16px;
    text-align: left;
    font-style: normal;
    text-transform: none;
  }
}
</style>
